import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Modal } from "@mui/material";
import type { ComponentType, ReactNode } from "react";
import {
  FullscreenSlotContext,
  type FullscreenSlotProps,
} from "../../../components/Wrappers/FullscreenSlot";
import type { OverlayProps } from "../../../components/Wrappers/OverlaySlot";
import { OverlayContext } from "../../../components/Wrappers/OverlaySlot";
import { useThemeContext } from "../../../globalContext/global-context";
import { hostThemes } from "../../../theme/hostThemes";
import type { QuestionHost } from "./types";

const OverlayFrame = ({
  host,
  open,
  children,
  size = "dialog",
  onClose,
  closeLabel,
  closeTestId,
  ariaLabel,
  ariaLabelledBy,
  testId,
}: OverlayProps & { host: QuestionHost }) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const dimensions = hostThemes[host].overlay;
  const fullscreen = size === "fullscreen";
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: theme.tokens.color.surface.overlay },
        },
      }}
    >
      <Box
        role="dialog"
        data-survey-root="true"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        data-testid={testId}
        sx={{
          position: fullscreen ? "absolute" : "relative",
          top: fullscreen ? 0 : undefined,
          left: fullscreen ? 0 : undefined,
          width: fullscreen
            ? "100vw"
            : `min(calc(100vw - ${theme.tokens.spacing["4"] * 2}px), ${size === "media" ? dimensions.mediaMaxWidth : dimensions.dialogMaxWidth}px)`,
          height: fullscreen ? "100dvh" : undefined,
          maxHeight: fullscreen
            ? undefined
            : `calc(100dvh - ${theme.tokens.spacing["4"] * 2}px)`,
          overflowY: "auto",
          bgcolor: fullscreen
            ? theme.tokens.color.surface.overlay
            : theme.tokens.color.surface.card,
          color: fullscreen
            ? theme.tokens.color.text.inverse
            : theme.tokens.color.text.primary,
          borderRadius: fullscreen ? 0 : `${theme.tokens.shape.radius.lg}px`,
          boxShadow: fullscreen ? undefined : theme.tokens.elevation["3"],
          padding: `${theme.tokens.spacing["4"]}px`,
          paddingTop:
            onClose && closeLabel
              ? `${theme.tokens.spacing["6"] + theme.tokens.spacing["4"]}px`
              : undefined,
        }}
      >
        {onClose && closeLabel && (
          <IconButton
            aria-label={closeLabel}
            data-testid={closeTestId}
            onClick={onClose}
            sx={{
              position: "absolute",
              top: theme.tokens.spacing["2"],
              right: theme.tokens.spacing["2"],
              color: fullscreen ? "inherit" : undefined,
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        {children}
      </Box>
    </Modal>
  );
};

export const ChatHostOverlay = (props: OverlayProps) => (
  <OverlayFrame host="chat" {...props} />
);

export const FormsHostOverlay = (props: OverlayProps) => (
  <OverlayFrame host="forms" {...props} />
);

const overlayByHost = {
  chat: ChatHostOverlay,
  forms: FormsHostOverlay,
} satisfies Record<QuestionHost, ComponentType<OverlayProps>>;

const FullscreenFrame = ({
  host,
  active,
  children,
  ariaLabel,
  testId,
}: FullscreenSlotProps & { host: QuestionHost }) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  return (
    <Box
      role={active ? "dialog" : undefined}
      aria-modal={active ? true : undefined}
      aria-label={active ? ariaLabel : undefined}
      data-testid={testId}
      data-fullscreen-host={host}
      sx={
        active
          ? {
              position: "fixed",
              inset: 0,
              zIndex: 1301,
              isolation: "isolate",
              overflow: "hidden",
              boxSizing: "border-box",
              width: "100vw",
              height: "100dvh",
              display: "flex",
              alignItems: "stretch",
              justifyContent: "stretch",
              backgroundColor: theme.tokens.color.surface.overlay,
              color: theme.tokens.color.text.inverse,
              "& > [data-question-media]": {
                width: "100%",
                height: "100%",
                maxWidth: "none",
                minWidth: 0,
                minHeight: 0,
                boxSizing: "border-box",
              },
            }
          : { display: "contents" }
      }
    >
      {children}
    </Box>
  );
};

const ChatHostFullscreen = (props: FullscreenSlotProps) => (
  <FullscreenFrame host="chat" {...props} />
);

const FormsHostFullscreen = (props: FullscreenSlotProps) => (
  <FullscreenFrame host="forms" {...props} />
);

const fullscreenByHost = {
  chat: ChatHostFullscreen,
  forms: FormsHostFullscreen,
} satisfies Record<QuestionHost, ComponentType<FullscreenSlotProps>>;

export const HostOverlayProvider = ({
  host,
  children,
}: {
  host: QuestionHost;
  children: ReactNode;
}) => (
  <FullscreenSlotContext.Provider value={fullscreenByHost[host]}>
    <OverlayContext.Provider value={overlayByHost[host]}>
      {children}
    </OverlayContext.Provider>
  </FullscreenSlotContext.Provider>
);
